import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../services/authService";
import Swal from "sweetalert2";
import { loadRolePermission, loadUserDetails } from "../../app/store/auth";
import OnboardingHexagon from "./OnboardingHexagon";

const OnboardingCardHub = () => {
  const [currentUser, setCurrentUser] = useState("");
  const [user, setUser] = useState("");
  const [rolePermission, setRolePermission] = useState([]);
  const [activeCategory, setActiveCategory] = useState("system-admin");
  const [isSectionCollapsed, setIsSectionCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState("cards"); // 'cards' | 'hexagon'

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.getCurrentUser();
      if (!user) return;
      const currentUser = await dispatch(loadUserDetails(user.id));
      if (!currentUser) return;
      setUser(user);
      setCurrentUser(currentUser);

      if (currentUser?.registrationType === "WP") {
        const permission = await dispatch(loadRolePermission(currentUser.roleId));
        if (permission !== "") {
          setRolePermission(permission);
        }
      }
    };

    fetchData();
  }, [dispatch]);

  const checkMenuPermission = (mid) => {
    if (!mid) return true;
    if (user?.type === "admin") return true;
    if (currentUser?.roleType === "Complete Application Ownership") return true;

    const mm = mid.split("_");
    let chk = false;
    if (mm[0] === "m") {
      const mR = rolePermission.filter(
        (x) => x.id === parseInt(mm[1]) && x.isChecked === true
      );
      if (mR.length > 0) {
        chk = true;
      } else {
        rolePermission
          .filter((x) => x.id === parseInt(mm[1]))
          .forEach((menu) => {
            const sMenu = menu.menus.filter((x) => x.isChecked === true);
            if (sMenu.length > 0) {
              chk = true;
            } else {
              menu.menus.forEach((ssmenu) => {
                if (ssmenu?.menus) {
                  const ssMenu = ssmenu.menus.filter((x) => x.isChecked === true);
                  if (ssMenu.length > 0) chk = true;
                }
              });
            }
          });
      }
    }
    if (mm[0] === "sm") {
      rolePermission
        .filter((x) => x.id === parseInt(mm[2]))
        .forEach((menu) => {
          const sMenu = menu.menus.filter(
            (x) => x.id === parseInt(mm[1]) && x.isChecked === true
          );
          if (sMenu.length > 0) {
            chk = true;
          } else {
            menu.menus.forEach((smenu) => {
              if (smenu?.menus) {
                const ssMenu = smenu.menus.filter((x) => x.isChecked === true);
                if (ssMenu.length > 0) chk = true;
              } else if (smenu?.isChecked) {
                chk = true;
              }
            });
          }
        });
    }
    if (mm[0] === "ssm") {
      rolePermission
        .filter((x) => x.id === parseInt(mm[3]))
        .forEach((menu) =>
          menu.menus
            .filter((x) => x.id === parseInt(mm[2]))
            .forEach((ssmenu) => {
              if (ssmenu?.menus) {
                const ssMenu = ssmenu.menus.filter(
                  (x) => x.id === parseInt(mm[1]) && x.isChecked === true
                );
                if (ssMenu.length > 0) chk = true;
              } else if (ssmenu?.isChecked) {
                chk = true;
              }
            })
        );
    }
    return chk;
  };

  const permissionAlert = () => {
    Swal.fire("You are not authorized", "", "error");
  };

  const handleNavClick = (e, path, permissionCode) => {
    if (permissionCode && !checkMenuPermission(permissionCode)) {
      e.preventDefault();
      permissionAlert();
      return;
    }
    if (path && path !== "#") {
      navigate(path);
    }
  };

  // Definition of all 7 major sections (including "My Tasks")
  const sections = [
    {
      id: "system-admin",
      title: "System Admin",
      fullTitle: "System Administration",
      description: "Manage users, entities, candidate, subcontractor, and recruiter configurations",
      icon: "fa fa-cogs",
      subsections: [
        {
          id: "main-users",
          title: "Main Users",
          icon: "fa fa-user-circle-o",
          primaryPath: "/WorkflowUser",
          permission: "ssm_1_1_1"
        },
        {
          id: "candidates-admin",
          title: "Candidates",
          icon: "fa fa-graduation-cap",
          primaryPath: "/ListCondidate/partner",
          permission: "ssm_3_2_1"
        },
        {
          id: "subcontractors-admin",
          title: "Subcontractors",
          icon: "fa fa-handshake-o",
          primaryPath: "/ListSubcontractor/partner",
          permission: "ssm_1_2_1"
        },
        {
          id: "recruiters-admin",
          title: "Recruiters",
          icon: "fa fa-users",
          primaryPath: "/Recruiter/partner",
          permission: "ssm_2_2_1"
        }
      ]
    },
    {
      id: "onboarding",
      title: "Onboarding",
      fullTitle: "Onboarding & Contracts",
      description: "Track candidate pipelines, verify requirements, and manage partner agreements",
      icon: "fa fa-file-text",
      subsections: [
        {
          id: "candidates-onboarding",
          title: "Candidates",
          icon: "fa fa-graduation-cap",
          primaryPath: "/ListCondidate/application",
          permission: "ssm_3_3_2"
        },
        {
          id: "subcontractors-onboarding",
          title: "Subcontractors",
          icon: "fa fa-handshake-o",
          primaryPath: "/ListSubcontractor/application",
          permission: "ssm_1_1_2"
        },
        {
          id: "recruiters-onboarding",
          title: "Recruiters",
          icon: "fa fa-users",
          primaryPath: "/Recruiter/application",
          permission: "ssm_2_2_2"
        }
      ]
    },
    {
      id: "invoices",
      title: "Invoices",
      fullTitle: "Invoices & Payments",
      description: "Manage monthly time log reporting, client billing, payments, and payment records",
      icon: "fa fa-money",
      subsections: [
        {
          id: "time-log-mgmt",
          title: "Time Log Management",
          icon: "fa fa-clock-o",
          primaryPath: "/ListCondidate/timelog",
          permission: "ssm_1_1_3"
        },
        {
          id: "invoices-payments",
          title: "Invoices and Payments",
          icon: "fa fa-money",
          primaryPath: "/ListCondidate/invoice",
          permission: "ssm_2_2_3"
        }
      ]
    },
    {
      id: "analytics",
      title: "Analytics",
      fullTitle: "Analytics & Reports",
      description: "Executive dashboards, entity metrics, custom business reports, and compliance docs",
      icon: "fa fa-bar-chart",
      subsections: [
        {
          id: "analytics-dashboard",
          title: "Dashboard",
          icon: "fa fa-tachometer",
          primaryPath: "/Dashboard",
          permission: "ssm_1_1_4"
        },
        {
          id: "entity-data",
          title: "Entity Data",
          icon: "fa fa-database",
          primaryPath: "/ApplicationReport",
          permission: "ssm_2_2_4"
        },
        {
          id: "business-reports",
          title: "Business Reports",
          icon: "fa fa-bar-chart",
          primaryPath: "/CustomReport/report",
          permission: "ssm_3_3_4"
        },
        {
          id: "compliance-reports",
          title: "Compliance Reports/Docs",
          icon: "fa fa-check-square-o",
          primaryPath: "/CustomReport/compliance",
          permission: "ssm_5_5_4"
        }
      ]
    },
    {
      id: "security",
      title: "Security",
      fullTitle: "Security & Monitoring",
      description: "System security, user access history, and real-time monitoring audit logs",
      icon: "fa fa-shield",
      subsections: [
        {
          id: "monitoring-logs",
          title: "Monitoring Logs",
          icon: "fa fa-shield",
          primaryPath: "/MonitoringLogs",
          permission: "ssm_1_1_5"
        }
      ]
    },
    {
      id: "communications",
      title: "Communications",
      fullTitle: "Communications & Messaging",
      description: "Email broadcasts, SMS text messaging, partner updates, and survey feedback",
      icon: "fa fa-comments",
      subsections: [
        {
          id: "comm-candidates-mail",
          title: "Candidates (Email)",
          icon: "fa fa-envelope",
          primaryPath: "/ApplicantEmails/mail",
          permission: "ssm_3_1_6"
        },
        {
          id: "comm-subcontractors-mail",
          title: "Subcontractors (Email)",
          icon: "fa fa-envelope-o",
          primaryPath: "/PartnerEmails/mail",
          permission: "ssm_1_1_6"
        },
        {
          id: "comm-recruiters-mail",
          title: "Recruiters (Email)",
          icon: "fa fa-envelope-open-o",
          primaryPath: "/AgencyEmails/mail",
          permission: "ssm_2_1_6"
        },
        {
          id: "comm-candidates-text",
          title: "Candidates (SMS)",
          icon: "fa fa-comments-o",
          primaryPath: "/ApplicantEmails/text",
          permission: "ssm_3_2_6"
        },
        {
          id: "comm-subcontractors-text",
          title: "Subcontractors (SMS)",
          icon: "fa fa-mobile",
          primaryPath: "/PartnerEmails/text",
          permission: "ssm_1_2_6"
        },
        {
          id: "comm-recruiters-text",
          title: "Recruiters (SMS)",
          icon: "fa fa-comment-o",
          primaryPath: "/AgencyEmails/text",
          permission: "ssm_2_2_6"
        },
        {
          id: "comm-surveys",
          title: "Surveys",
          icon: "fa fa-commenting-o",
          primaryPath: "/SurveyFeedback",
          permission: "ssm_5_5_6"
        }
      ]
    },
    {
      id: "my-tasks",
      title: "My Tasks",
      fullTitle: "My Tasks & Assigned Work",
      description: "Personal assigned workflow tasks, pending approvals, and scheduled action items",
      icon: "fa fa-tasks",
      isTasksPlaceholder: true,
      subsections: []
    }
  ];

  const currentSection = sections.find((s) => s.id === activeCategory) || sections[0];

  // If user has switched to classic hexagon view, render hexagon with toggle support
  if (viewMode === "hexagon") {
    return <OnboardingHexagon onSwitchToCards={() => setViewMode("cards")} />;
  }

  return (
    <div id="content" className="och-container">
      <style>{`
        .och-container {
          min-height: 100%;
          width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
          flex: 1 1 auto !important;
          background: #f8fafc;
          padding: 20px 28px 40px;
          box-sizing: border-box;
          font-family: inherit;
        }

        .och-top-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .och-breadcrumb-area {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #475569;
          font-size: 13px;
          font-weight: 500;
        }

        .och-view-toggle {
          display: flex;
          align-items: center;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          padding: 3px;
          gap: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }

        .och-toggle-btn {
          border: none;
          background: transparent;
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }

        .och-toggle-btn.active {
          background: #0f172a;
          color: #ffffff;
          box-shadow: 0 2px 4px rgba(15, 23, 42, 0.12);
        }

        /* Top row of major category cards (7 items) */
        .och-major-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 12px;
          margin-bottom: 22px;
        }

        @media (max-width: 1380px) {
          .och-major-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        @media (max-width: 900px) {
          .och-major-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 600px) {
          .och-major-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .och-major-card {
          background: #ffffff;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px 12px 14px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
          position: relative;
          user-select: none;
        }

        .och-major-card:hover {
          border-color: #0f172a;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
        }

        .och-major-card.active {
          border-color: #0f172a;
          background: #ffffff;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
        }

        .och-major-card.active::before {
          content: "";
          position: absolute;
          top: 0;
          left: 12px;
          right: 12px;
          height: 3px;
          background: #0f172a;
          border-radius: 0 0 3px 3px;
        }

        .och-major-icon-box {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: #f1f5f9;
          color: #0f172a;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          margin-bottom: 9px;
          transition: all 0.2s ease;
          border: 1px solid #e2e8f0;
        }

        .och-major-card.active .och-major-icon-box {
          background: #0f172a;
          color: #ffffff;
          border-color: #0f172a;
        }

        .och-major-title {
          font-size: 13.5px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 7px;
          line-height: 1.25;
        }

        .och-major-badge {
          font-size: 11px;
          font-weight: 600;
          color: #475569;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 3px 8px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: all 0.2s ease;
        }

        .och-major-card.active .och-major-badge {
          background: #0f172a;
          color: #ffffff;
          border-color: #0f172a;
        }

        /* Expanded section below */
        .och-detail-section {
          background: #ffffff;
          border: 1.5px solid #e2e8f0;
          border-radius: 14px;
          box-shadow: 0 4px 18px rgba(15, 23, 42, 0.05);
          overflow: hidden;
          transition: all 0.25s ease;
        }

        .och-detail-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 24px;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
          cursor: pointer;
          user-select: none;
        }

        .och-detail-header-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .och-detail-header-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #0f172a;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          flex-shrink: 0;
        }

        .och-detail-header-text h3 {
          margin: 0 0 3px;
          font-size: 17px;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.2;
        }

        .och-detail-header-text p {
          margin: 0;
          font-size: 13px;
          color: #64748b;
          font-weight: 500;
        }

        .och-detail-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .och-collapse-trigger {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #334155;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          transition: all 0.2s ease;
        }

        .och-collapse-trigger:hover {
          background: #0f172a;
          color: #ffffff;
          border-color: #0f172a;
        }

        .och-detail-body {
          padding: 24px;
          background: #fafafa;
        }

        /* Sleek Thin Rectangular Subcards Grid */
        .och-sub-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
          gap: 14px;
        }

        .och-thin-subcard {
          background: #ffffff;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          text-decoration: none !important;
          color: #0f172a;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.03);
          user-select: none;
        }

        .och-thin-subcard:hover {
          border-color: #0f172a;
          background: #ffffff;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
        }

        .och-thin-subcard-left {
          display: flex;
          align-items: center;
          gap: 12px;
          overflow: hidden;
        }

        .och-thin-icon {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          background: #f1f5f9;
          color: #0f172a;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          flex-shrink: 0;
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }

        .och-thin-subcard:hover .och-thin-icon {
          background: #0f172a;
          color: #ffffff;
          border-color: #0f172a;
        }

        .och-thin-title {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .och-thin-arrow {
          font-size: 13px;
          color: #94a3b8;
          transition: all 0.2s ease;
          flex-shrink: 0;
          margin-left: 8px;
        }

        .och-thin-subcard:hover .och-thin-arrow {
          color: #0f172a;
        }

        /* Coming Soon Placeholder for My Tasks */
        .och-placeholder-card {
          background: #ffffff;
          border: 1.5px dashed #cbd5e1;
          border-radius: 12px;
          padding: 48px 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          max-width: 620px;
          margin: 0 auto;
        }

        .och-placeholder-icon-box {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #0f172a;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          margin-bottom: 16px;
        }

        .och-placeholder-badge {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          background: #0f172a;
          color: #ffffff;
          padding: 4px 10px;
          border-radius: 20px;
          margin-bottom: 10px;
        }

        .och-placeholder-title {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 8px;
        }

        .och-placeholder-desc {
          font-size: 13.5px;
          color: #64748b;
          line-height: 1.55;
          max-width: 460px;
          margin: 0 0 20px;
        }

        .och-placeholder-chips {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 8px;
        }

        .och-placeholder-chip {
          font-size: 11.5px;
          font-weight: 600;
          color: #334155;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 4px 10px;
        }
      `}</style>

      {/* Top Header / Breadcrumb & View Toggle */}
      <div className="och-top-bar">
        <div className="och-breadcrumb-area">
          <i className="fa fa-home" />
          <span>Quick Links</span>
          <i className="fa fa-angle-right" style={{ color: "#94a3b8" }} />
          <span style={{ color: "#0f172a", fontWeight: 700 }}>
            {currentSection?.fullTitle || "Onboarding Portal"}
          </span>
        </div>

        <div className="och-view-toggle">
          <button
            type="button"
            className={`och-toggle-btn ${viewMode === "cards" ? "active" : ""}`}
            onClick={() => setViewMode("cards")}
            title="Card View (Clean Black & White)"
          >
            <i className="fa fa-th-large" /> Card View
          </button>
          <button
            type="button"
            className={`och-toggle-btn ${viewMode === "hexagon" ? "active" : ""}`}
            onClick={() => setViewMode("hexagon")}
            title="Classic Honeycomb Hexagon View"
          >
            <i className="fa fa-cube" /> Hexagon View
          </button>
        </div>
      </div>

      {/* Row of 7 Major Category Cards */}
      <div className="och-major-grid">
        {sections.map((sec) => {
          const isActive = activeCategory === sec.id;
          return (
            <div
              key={sec.id}
              className={`och-major-card ${isActive ? "active" : ""}`}
              onClick={() => {
                if (activeCategory === sec.id) {
                  setIsSectionCollapsed(!isSectionCollapsed);
                } else {
                  setActiveCategory(sec.id);
                  setIsSectionCollapsed(false);
                }
              }}
            >
              <div className="och-major-icon-box">
                <i className={sec.icon} />
              </div>
              <h4 className="och-major-title">{sec.title}</h4>
              <span className="och-major-badge">
                {sec.isTasksPlaceholder ? (
                  <>Coming Soon <i className="fa fa-clock-o" /></>
                ) : (
                  <>{sec.subsections.length} Functions <i className="fa fa-angle-right" /></>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Expanded Subsection Panel for the Selected Major Category */}
      {currentSection && (
        <div className="och-detail-section">
          {/* Header */}
          <div
            className="och-detail-header"
            onClick={() => setIsSectionCollapsed(!isSectionCollapsed)}
          >
            <div className="och-detail-header-left">
              <div className="och-detail-header-icon">
                <i className={currentSection.icon} />
              </div>
              <div className="och-detail-header-text">
                <h3>{currentSection.fullTitle}</h3>
                <p>{currentSection.description}</p>
              </div>
            </div>
            <div className="och-detail-header-right">
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#64748b",
                  background: "#f1f5f9",
                  padding: "4px 10px",
                  borderRadius: "6px"
                }}
              >
                {currentSection.isTasksPlaceholder
                  ? "Assigned Tasks"
                  : `${currentSection.subsections.length} Subsections`}
              </span>
              <button
                type="button"
                className="och-collapse-trigger"
                aria-label={isSectionCollapsed ? "Expand" : "Collapse"}
              >
                <i
                  className={`fa ${isSectionCollapsed ? "fa-chevron-down" : "fa-chevron-up"}`}
                />
              </button>
            </div>
          </div>

          {/* Subsections Content */}
          {!isSectionCollapsed && (
            <div className="och-detail-body">
              {currentSection.isTasksPlaceholder ? (
                /* "My Tasks" Coming Soon Placeholder */
                <div className="och-placeholder-card">
                  <div className="och-placeholder-icon-box">
                    <i className="fa fa-tasks" />
                  </div>
                  <span className="och-placeholder-badge">Coming Soon</span>
                  <h4 className="och-placeholder-title">My Tasks & Workflow Actions</h4>
                  <p className="och-placeholder-desc">
                    Your assigned onboarding tasks, candidate document reviews, and workflow approval items will be available right here soon.
                  </p>
                  <div className="och-placeholder-chips">
                    <span className="och-placeholder-chip">✓ Assigned Candidate Tasks</span>
                    <span className="och-placeholder-chip">✓ Pending Approvals</span>
                    <span className="och-placeholder-chip">✓ Document Verifications</span>
                    <span className="och-placeholder-chip">✓ Due Dates & Alerts</span>
                  </div>
                </div>
              ) : (
                /* Thin Rectangular Subcards (only icon + heading, clickable whole card) */
                <div className="och-sub-grid">
                  {currentSection.subsections.map((sub) => (
                    <Link
                      key={sub.id}
                      to={sub.primaryPath}
                      className="och-thin-subcard"
                      onClick={(e) => handleNavClick(e, sub.primaryPath, sub.permission)}
                    >
                      <div className="och-thin-subcard-left">
                        <div className="och-thin-icon">
                          <i className={sub.icon} />
                        </div>
                        <span className="och-thin-title">{sub.title}</span>
                      </div>
                      <i className="fa fa-arrow-right och-thin-arrow" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OnboardingCardHub;
